import { validateSalesToolFile } from "../providers/commons/salesToolFiles";
import type { SalesToolKind } from "../types";

/** Kinds that are plain text: they never carry a file. */
export const TEXT_ONLY_TOOL_KINDS: SalesToolKind[] = [
  "script",
  "question",
  "objection",
];

/** The editable values of the tool form. */
export type SalesToolFormValues = {
  title: string;
  kind: SalesToolKind | "";
  body: string;
  videoUrl: string;
  /** A newly picked file, not yet uploaded. */
  file: Pick<File, "type" | "size"> | null;
  /** True when the edited tool already has a stored file. */
  hasExistingFile: boolean;
};

/** i18n key suffix (under `crm.salesTools.form.errors`) per invalid field. */
export type SalesToolFormErrors = Partial<
  Record<"title" | "kind" | "content" | "video_url" | "file", string>
>;

const isHttpUrl = (value: string) => /^https?:\/\/\S+$/i.test(value);

/**
 * Validates the tool form. Returns an error-key map; empty when valid. The UI owns the Spanish
 * labels. Rules: title and kind required; some content (text, link or file) required; text-only
 * kinds accept no file; a link must be http(s); a new file must pass the shared file validator.
 */
export const validateSalesToolForm = (
  values: SalesToolFormValues,
): SalesToolFormErrors => {
  const errors: SalesToolFormErrors = {};
  if (!values.title.trim()) errors.title = "title_required";
  if (!values.kind) errors.kind = "kind_required";

  const videoUrl = values.videoUrl.trim();
  if (videoUrl && !isHttpUrl(videoUrl)) errors.video_url = "video_url_invalid";

  const hasFile = values.file != null || values.hasExistingFile;
  if (!values.body.trim() && !videoUrl && !hasFile) {
    errors.content = "content_required";
  }

  if (values.kind && TEXT_ONLY_TOOL_KINDS.includes(values.kind) && hasFile) {
    errors.file = "file_not_allowed";
  } else if (values.file) {
    const result = validateSalesToolFile(values.file);
    if (!result.valid) errors.file = result.reason;
  }
  return errors;
};

/** Adds or removes `value` from `list`, returning a new array. */
export const toggleTag = <T extends string | number>(
  list: T[],
  value: T,
): T[] =>
  list.includes(value)
    ? list.filter((item) => item !== value)
    : [...list, value];
